import { Injectable } from '@angular/core';

@Injectable({providedIn: 'root'})
export class LocalStorageFacade {
  private readonly actuarPrefixKey = 'actuar/';
  private readonly customEventName = 'actuar-storage';

  // private emitChange(key: string, value: string | null): void {
  //   try {
  //     if (typeof window !== 'undefined' && 'dispatchEvent' in window) {
  //       window.dispatchEvent(new CustomEvent(this.customEventName, { detail: { key, value } }));
  //     }
  //   } catch {
  //     // noop in non-browser or event dispatch failure
  //   }
  // }

  /** Resolved once — localStorage availability does not change during the app lifecycle. */
  private _storage?: Storage | null;

  /**
   * Safely resolves the global `localStorage`. Returns `null` when it is
   * unavailable — e.g. embedded WebViews or Safari with storage blocked, where
   * accessing the global throws `ReferenceError: Can't find variable: localStorage`.
   */
  private get storage(): Storage | null {
    if (this._storage === undefined) {
      try {
        this._storage = typeof localStorage !== 'undefined' ? localStorage : null;
      } catch {
        this._storage = null;
      }
    }
    return this._storage;
  }

  public setItem(key: string, value: any): void {
    const _key: string = this.actuarPrefixKey + key;
    this.storage?.setItem(_key, value);
    // this.emitChange(_key, value);
  }

  public getItem(key: string): any {
    const _key: string = this.actuarPrefixKey + key;
    return this.storage?.getItem(_key) ?? null;
  }

  public removeItem(key: string): void {
    const _key: string = this.actuarPrefixKey + key;
    this.storage?.removeItem(_key);
    // this.emitChange(_key, null);
  }

  /**
   * Every `actuar/`-namespaced key currently stored, returned without the
   * prefix so it reads back the same way `getItem`/`removeItem` take it.
   *
   * For sweeping a family of keys whose exact names are not known — retiring a
   * storage format, say, where every key ever written under an old prefix has
   * to go. Enumerating is O(n) over the whole store, so keep it out of paths
   * that run per interaction.
   */
  public keys(): string[] {
    const storage = this.storage;
    if (!storage) return [];

    const keys: string[] = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key?.startsWith(this.actuarPrefixKey)) {
        keys.push(key.slice(this.actuarPrefixKey.length));
      }
    }
    return keys;
  }

  public clearAllData(): void {
    this.storage?.clear();
    // this.emitChange('*', null);
  }

  /**
   * Literal-key (un-prefixed) safe accessors. Use these for keys that are NOT
   * namespaced under `actuar/` — e.g. `language`, `credentials`, `activeAccount`,
   * third-party SDK keys — where prepending the prefix would change the stored
   * key and break existing data. All reads/writes still go through the guarded
   * `storage` getter, so a blocked-storage `SecurityError` never escapes.
   */
  public getItemRaw(key: string): string | null {
    return this.storage?.getItem(key) ?? null;
  }

  public setItemRaw(key: string, value: string): void {
    this.storage?.setItem(key, value);
  }

  public removeItemRaw(key: string): void {
    this.storage?.removeItem(key);
  }
}
